import { useState, useEffect, useRef } from 'react';
import { lookupPostalCode } from '../utils/onemap';

export function usePostalLookup(
  postalValue: string | undefined,
  enabled: boolean = true,
  onAddressFound: (address: { BLK_NO: string; ROAD_NAME: string; BUILDING: string }) => void,
) {
  const [status, setStatus] = useState<string>('');
  const [isSearching, setIsSearching] = useState(false);

  // Callers pass a fresh closure every render. Capture via ref so the effect
  // deps stay stable — otherwise each updateCustomerField from onAddressFound
  // re-renders, creates a new callback, re-fires the lookup, and a transient
  // OneMap glitch overwrites the successful result with "Postal code not found".
  const onAddressFoundRef = useRef(onAddressFound);
  onAddressFoundRef.current = onAddressFound;

  useEffect(() => {
    if (!enabled || !postalValue) {
      setStatus('');
      setIsSearching(false);
      return;
    }

    const cleaned = postalValue.replace(/\D/g, '');
    if (cleaned.length !== 6) {
      setStatus('');
      setIsSearching(false);
      return;
    }

    const controller = new AbortController();

    const fetchAddress = async () => {
      setIsSearching(true);
      try {
        const address = await lookupPostalCode(cleaned, controller.signal);
        if (controller.signal.aborted) return;
        if (address) {
          onAddressFoundRef.current(address);
          setStatus('✓ Address synced via OneMap');
        } else {
          setStatus('Postal code not found');
        }
      } catch (e) {
        const err = e as { name?: string } | null | undefined;
        if (controller.signal.aborted || err?.name === 'AbortError') return;
        setStatus('OneMap service unavailable');
      } finally {
        if (!controller.signal.aborted) setIsSearching(false);
      }
    };

    const timer = setTimeout(fetchAddress, 400);
    return () => {
      clearTimeout(timer);
      controller.abort();
      setIsSearching(false);
    };
  }, [postalValue, enabled]);

  return { status, isSearching };
}
